import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

const STORAGE_KEY = 'sidebar_expanded';
const DESKTOP_BREAKPOINT = 768;
const SIDEBAR_WIDTH_EXPANDED = 290;
const SIDEBAR_WIDTH_COLLAPSED = 80;

interface SidebarContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  isMobile: boolean;
  /** No desktop, true quando o mouse está sobre o sidebar com menu recolhido (expansão temporária) */
  hoverExpanded: boolean;
  setHoverExpanded: (v: boolean) => void;
  sidebarWidth: number;
  /** Largura usada para margin do main (0 no mobile quando overlay) */
  mainMargin: number;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

function getIsMobile() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < DESKTOP_BREAKPOINT;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpenState] = useState(true);
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [hoverExpanded, setHoverExpanded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null && !getIsMobile()) {
      setIsOpenState(stored === 'true');
    }
    if (getIsMobile()) setIsOpenState(false);
  }, []);

  useEffect(() => {
    const check = () => {
      const mobile = getIsMobile();
      setIsMobile(mobile);
      if (mobile) setIsOpenState(false);
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState((prev) => {
      const next = open;
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        localStorage.setItem(STORAGE_KEY, String(next));
      }
      return next;
    });
  }, []);

  const toggle = useCallback(() => {
    setIsOpenState((prev) => {
      const next = !prev;
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        localStorage.setItem(STORAGE_KEY, String(next));
      }
      return next;
    });
  }, []);

  const expandedByHoverOrChoice = isOpen || hoverExpanded;
  const sidebarWidth = isMobile
    ? (isOpen ? SIDEBAR_WIDTH_EXPANDED : 0)
    : (expandedByHoverOrChoice ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED);
  const mainMargin = isMobile ? 0 : sidebarWidth;

  const value: SidebarContextValue = {
    isOpen,
    setIsOpen,
    toggle,
    isMobile,
    hoverExpanded,
    setHoverExpanded,
    sidebarWidth,
    mainMargin,
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}

export { DESKTOP_BREAKPOINT, SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED };
